import React, { useEffect, useState } from "react";
import ControlLayout from "../components/ControlLayout";
import { api, formatDetail } from "../lib/api";
import { Plus, Camera, KeyRound, Copy, Check } from "lucide-react";

export default function ControlEmployees() {
  const [employees, setEmployees] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", display_name: "", role: "", outlet_id: "",
    confidence_profile: "HIGH", photo_url: "",
  });
  const [error, setError] = useState("");
  const [enrollFor, setEnrollFor] = useState(null);

  const refresh = async () => {
    const [e, o] = await Promise.all([api.get("/employees"), api.get("/outlets")]);
    setEmployees(e.data);
    setOutlets(o.data);
    setForm((f) => ({ ...f, outlet_id: f.outlet_id || o.data[0]?.id || "" }));
  };
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    setError("");
    try {
      await api.post("/employees", form);
      setOpen(false);
      setForm((f) => ({ ...f, code: "", name: "", display_name: "", role: "", photo_url: "" }));
      refresh();
    } catch (e) { setError(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <ControlLayout
      title="Employees"
      subtitle="Roster · face profile status · enrollment codes"
      actions={
        <button
          data-testid="btn-enroll-open"
          onClick={() => setOpen(true)}
          className="h-11 px-4 bg-black text-white font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
        >
          <Plus size={14} /> Add employee
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="panel">
            <div className="panel-header">
              <span className="mono-label">Roster · {employees.length}</span>
              <span className="mono-label text-neutral-400">Face profiles owned by People Engine</span>
            </div>
            <table className="w-full text-sm" data-testid="employees-table">
              <thead className="bg-[#FAFAFA] border-b border-neutral-200">
                <tr className="text-left">
                  <Th>Employee</Th><Th>Code</Th><Th>Role</Th><Th>Face profile</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`emp-row-${e.code}`}>
                    <td className="px-4 py-3 flex items-center gap-3">
                      {e.photo_url ? <img src={e.photo_url} alt="" className="w-10 h-10 object-cover grayscale border border-neutral-200" /> : <div className="w-10 h-10 bg-neutral-100 border border-neutral-200" />}
                      <div>
                        <div className="font-semibold">{e.name}</div>
                        <div className="mono-label" style={{ fontSize: 9 }}>{e.outlet_id}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono">{e.code}</td>
                    <td className="px-4 py-3">{e.role}</td>
                    <td className="px-4 py-3">
                      <FaceProfilePill status={e.face_profile_status} count={e.face_profile_image_count} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`btn-gen-face-code-${e.code}`}
                        onClick={() => setEnrollFor(e)}
                        className="h-9 px-3 border border-neutral-900 bg-white font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1.5 hover:bg-neutral-100"
                      >
                        <KeyRound size={11} />
                        {e.face_profile_status === "ENROLLED" ? "Re-enrol face" : "Enrol face"}
                      </button>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && <tr><td colSpan={5} className="p-8 text-center mono-label">No employees.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4">
          <div className="panel">
            <div className="panel-header"><span className="mono-label">Face enrollment policy</span></div>
            <ul className="p-4 space-y-2 text-sm text-neutral-700">
              <li>· Codes are single-use and expire in 10 minutes.</li>
              <li>· 3 captures required: front, left, right.</li>
              <li>· Clear lighting; no masks/caps/sunglasses.</li>
              <li>· Capture happens on the paired Presence device.</li>
              <li>· People Engine stores and owns the mapping.</li>
            </ul>
          </div>
          {open && (
            <div className="panel mt-6" data-testid="enroll-panel">
              <div className="panel-header">
                <span className="mono-label">Add employee</span>
                <button onClick={() => setOpen(false)} className="mono-label hover:underline">Close</button>
              </div>
              <div className="p-4 space-y-3">
                <Field label="Code"><input data-testid="enroll-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inp} /></Field>
                <Field label="Name"><input data-testid="enroll-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
                <Field label="Display name"><input data-testid="enroll-display" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={inp} /></Field>
                <Field label="Role"><input data-testid="enroll-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp} /></Field>
                <Field label="Outlet">
                  <select data-testid="enroll-outlet" value={form.outlet_id} onChange={(e) => setForm({ ...form, outlet_id: e.target.value })} className={inp}>
                    {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Field>
                <Field label="Sim profile">
                  <select data-testid="enroll-profile" value={form.confidence_profile} onChange={(e) => setForm({ ...form, confidence_profile: e.target.value })} className={inp}>
                    <option value="HIGH">HIGH</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="LOW">LOW</option>
                    <option value="MIXED">MIXED</option>
                  </select>
                </Field>
                <Field label="Photo URL (optional)"><input data-testid="enroll-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className={inp} placeholder="https://…" /></Field>
                {error && <div className="text-red-700 font-mono text-sm">{error}</div>}
                <button data-testid="btn-submit-enroll" onClick={submit} disabled={!form.code || !form.name || !form.role} className="w-full h-12 bg-black text-white font-mono text-sm uppercase tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  <Camera size={14} /> Save employee
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {enrollFor && (
        <FaceEnrollmentCodeModal
          employee={enrollFor}
          onClose={() => { setEnrollFor(null); refresh(); }}
        />
      )}
    </ControlLayout>
  );
}

function FaceEnrollmentCodeModal({ employee, onClose }) {
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [outletName, setOutletName] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.post("/people-engine/face-enrollments/codes", {
          employee_id: employee.id,
        });
        if (!mounted) return;
        setCode(r.data.code);
        setExpiresAt(new Date(r.data.expires_at_iso).getTime());
        setOutletName(r.data.outlet_name);
      } catch (e) {
        if (!mounted) return;
        setError(formatDetail(e.response?.data?.detail));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [employee.id]);

  useEffect(() => {
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const remainingMs = Math.max(0, (expiresAt || 0) - nowMs);
  const remainingMin = Math.floor(remainingMs / 60000);
  const remainingSec = Math.floor((remainingMs % 60000) / 1000);
  const expired = expiresAt && remainingMs === 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="face-code-modal"
    >
      <div className="w-full max-w-lg bg-white border border-neutral-900 p-8 m-4">
        <div className="mono-label" style={{ fontSize: 10 }}>FACE ENROLLMENT CODE</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter mt-2" data-testid="face-code-title">
          {employee.name}
        </h2>
        <p className="mt-2 mono-label text-neutral-500">
          {employee.code} · {outletName || employee.outlet_id}
        </p>
        {loading && <p className="mt-6 mono-label">Generating code…</p>}
        {error && <p className="mt-6 text-red-700 font-mono text-sm" data-testid="face-code-error">{error}</p>}
        {code && !error && (
          <>
            <div className="mt-6 flex items-stretch gap-3">
              <div
                data-testid="face-code-value"
                className="flex-1 h-20 border-2 border-black bg-[#FAFAFA] font-mono text-4xl tracking-[0.4em] flex items-center justify-center"
              >
                {code}
              </div>
              <button
                type="button"
                onClick={copy}
                data-testid="face-code-copy"
                className="h-20 w-20 border-2 border-black bg-white inline-flex items-center justify-center hover:bg-neutral-100"
                title="Copy to clipboard"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <p
              className={`mt-3 mono-label ${expired ? "text-red-700" : "text-neutral-600"}`}
              data-testid="face-code-countdown"
            >
              {expired
                ? "EXPIRED · GENERATE A NEW CODE"
                : `EXPIRES IN ${String(remainingMin).padStart(2, "0")}:${String(remainingSec).padStart(2, "0")} · SINGLE-USE`}
            </p>
            <div className="mt-6 border border-neutral-200 bg-[#FAFAFA] p-4 text-sm text-neutral-700 leading-relaxed">
              <div className="mono-label text-neutral-500 mb-2">NEXT STEP</div>
              Give this code to {employee.display_name || employee.name}. On
              the paired Presence device, tap <strong>Enrol a face</strong>,
              enter this code, and capture front / left / right.
            </div>
          </>
        )}
        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            data-testid="face-code-close"
            className="h-12 px-5 bg-black text-white font-mono text-xs uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function FaceProfilePill({ status, count }) {
  const map = {
    ENROLLED: { cls: "border-[#22C55E] bg-[#DCFCE7] text-[#166534]", label: count ? `ENROLLED · ${count}` : "ENROLLED" },
    NONE: { cls: "border-neutral-300 bg-neutral-100 text-neutral-700", label: "NOT ENROLLED" },
  };
  const m = map[status] || map.NONE;
  return (
    <span className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 mono-label text-left" style={{ fontSize: 10 }}>{children}</th>;
const inp = "w-full h-11 px-3 border border-neutral-300 bg-white font-mono text-sm focus:outline-none focus:border-black";
const Field = ({ label, children }) => (<div><label className="mono-label">{label}</label><div className="mt-1">{children}</div></div>);
